import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { TourStep } from './tours';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 6;
const GAP = 12;
const CARD_WIDTH = 340;
/** الارتفاع التقريبى للبطاقة — لتقرير هل تتسع تحت العنصر أو فوقه. */
const CARD_ROOM = 230;
/** تحت هذا العرض تصير البطاقة شريطًا ثابتًا أسفل الشاشة بدل فقاعة بجانب العنصر. */
const SHEET_BELOW = 640;

const toArabic = (n: number) => n.toLocaleString('ar-EG');

/** أول عنصر ظاهر يطابق الخطوة — العنصر المخفى (درج القائمة على الموبايل) لا يُضاء. */
export function findTarget(step: TourStep): HTMLElement | null {
  if (!step.target) return null;
  const selectors = typeof step.target === 'string' ? [step.target] : step.target;
  for (const selector of selectors) {
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(selector))) {
      if (el.closest('.tour-root')) continue;
      if (step.text && !el.textContent?.includes(step.text)) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      if (r.right <= 0 || r.left >= window.innerWidth) continue;
      if (getComputedStyle(el).visibility === 'hidden') continue;
      return el;
    }
  }
  return null;
}

function measure(el: HTMLElement | null): Rect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // العنصر الأطول من الشاشة يُضاء جزؤه الظاهر فقط، وإلا خرجت البطاقة عن الشاشة
  const top = Math.max(r.top, 8);
  const bottom = Math.min(r.bottom, window.innerHeight - 8);
  return { top, left: r.left, width: r.width, height: Math.max(bottom - top, 24) };
}

/**
 * جولة تعريفية: تظلّل الشاشة وتضىء عنصرًا وتشرحه فى بطاقة. بلا مكتبة خارجية — موضع
 * البطاقة يُحسب من مستطيل العنصر، وعلى الموبايل تثبت البطاقة أسفل الشاشة.
 * الأسهم تتبع اتجاه القراءة: ← التالى و→ السابق، وEsc ينهى.
 */
export default function Tour({ steps: all, onClose }: { steps: TourStep[]; onClose: () => void }) {
  // خطوة اختيارية بلا عنصرها (لوحة فارغة فى شركة جديدة) تسقط بدل شرح شىء غير ظاهر
  const [steps] = useState(() => all.filter((s) => !s.optional || findTarget(s)));
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const step = steps[index];
  const last = index === steps.length - 1;

  const relayout = useCallback(() => {
    if (!step) return;
    setViewport({ w: window.innerWidth, h: window.innerHeight });
    setRect(measure(findTarget(step)));
  }, [step]);

  useLayoutEffect(() => {
    if (!step) return;
    const el = findTarget(step);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const r = el?.getBoundingClientRect();
    // العنصر الظاهر كاملاً لا يُحرَّك، والأطول من الشاشة يُعرض أوله
    if (el && r && (r.top < 0 || r.bottom > window.innerHeight)) {
      el.scrollIntoView({
        block: r.height > window.innerHeight * 0.8 ? 'start' : 'center',
        inline: 'nearest',
        behavior: reduce ? 'auto' : 'smooth',
      });
    }
    setRect(measure(el));
    // التمرير الناعم والبيانات التى تصل متأخرة تحرّك العنصر — نعيد القياس حتى يستقر
    const timer = window.setInterval(relayout, 250);
    return () => window.clearInterval(timer);
  }, [step, relayout]);

  useEffect(() => {
    window.addEventListener('resize', relayout);
    window.addEventListener('scroll', relayout, true);
    return () => {
      window.removeEventListener('resize', relayout);
      window.removeEventListener('scroll', relayout, true);
    };
  }, [relayout]);

  useEffect(() => {
    cardRef.current?.focus();
  }, [index]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    return () => previous?.focus?.();
  }, []);

  const next = useCallback(() => {
    if (last) onClose();
    else setIndex((i) => i + 1);
  }, [last, onClose]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        next();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        back();
      } else if (event.key === 'Tab' && cardRef.current) {
        // التركيز محبوس فى البطاقة: الصفحة خلف الظل غير قابلة للتفاعل أثناء الجولة
        const focusable = Array.from(cardRef.current.querySelectorAll<HTMLElement>('button'));
        const first = focusable[0];
        const lastButton = focusable[focusable.length - 1];
        if (!first || !lastButton) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          lastButton.focus();
        } else if (!event.shiftKey && document.activeElement === lastButton) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, onClose]);

  if (!step) return null;

  const sheet = viewport.w < SHEET_BELOW;
  let cardStyle: CSSProperties | undefined;
  if (!sheet && rect) {
    const width = Math.min(CARD_WIDTH, viewport.w - 32);
    const below = rect.top + rect.height + PAD + GAP;
    const above = rect.top - PAD - GAP;
    // RTL: البطاقة تبدأ من الحافة اليمنى للعنصر، وتُحصر داخل الشاشة
    const right = Math.min(
      Math.max(16, viewport.w - (rect.left + rect.width)),
      viewport.w - width - 16,
    );
    const besideRight = viewport.w - rect.left + PAD + GAP;
    if (below + CARD_ROOM < viewport.h) {
      cardStyle = { top: below, right, width };
    } else if (above > CARD_ROOM) {
      cardStyle = { bottom: viewport.h - above, right, width };
    } else if (besideRight + width + 16 <= viewport.w) {
      // عنصر بطول الشاشة (القائمة مثلاً): البطاقة بجانبه لا فوقه ولا تحته
      cardStyle = {
        top: Math.max(16, Math.min(rect.top, viewport.h - CARD_ROOM)),
        right: besideRight,
        width,
      };
    } else {
      cardStyle = { bottom: 16, right: (viewport.w - width) / 2, width };
    }
  }

  return (
    <div className="tour-root">
      <div className="tour-backdrop" onClick={onClose} aria-hidden="true" />
      {rect ? (
        <div
          key="spot"
          className="tour-spot"
          aria-hidden="true"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      ) : (
        <div key="dim" className="tour-dim" aria-hidden="true" />
      )}
      <div
        ref={cardRef}
        className={'tour-card' + (sheet ? ' is-sheet' : rect ? '' : ' is-center')}
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
      >
        <p className="tour-count">
          {toArabic(index + 1)} من {toArabic(steps.length)}
        </p>
        <h2 id={titleId} className="tour-title">
          {step.title}
        </h2>
        <p id={bodyId} className="tour-body">
          {step.body}
        </p>
        <div className="tour-dots" aria-hidden="true">
          {steps.map((_, i) => (
            <span key={i} className={i === index ? 'is-on' : undefined} />
          ))}
        </div>
        <div className="tour-actions">
          <button type="button" className="primary" onClick={next}>
            {last ? 'تمام، فهمت' : 'التالى'}
          </button>
          {index > 0 && (
            <button type="button" className="ghost" onClick={back}>
              السابق
            </button>
          )}
          <span className="tour-spacer" />
          {!last && (
            <button type="button" className="link" onClick={onClose}>
              تخطَّ الجولة
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
